/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * BootstrapPage - Futuristic System Initialization Interface
 * ============================================================================
 *
 * Cyberpunk command center aesthetic for first-time SuperAdmin setup.
 * Features animated grid background, terminal boot sequence, and neon glows.
 *
 * ============================================================================
 */

import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, Terminal, Shield, Cpu, Database, Lock, CheckCircle2, AlertTriangle } from 'lucide-react';
import { useBootstrapStatus, useBootstrap } from '@/hooks/api/useAuth';
import { LanguageToggle } from '@/components/ui/LanguageToggle';
import { isApiError } from '@/services/api/errors';

export function BootstrapPage() {
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const { data: status, isLoading: statusLoading, error: statusError } = useBootstrapStatus();
  const bootstrapMutation = useBootstrap();

  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Animation state
  const [bootMessages, setBootMessages] = useState<{ text: string; delay: number; highlight?: boolean }[]>([]);
  const [bootComplete, setBootComplete] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [initProgress, setInitProgress] = useState(0);
  const terminalRef = useRef<HTMLDivElement>(null);
  const bootStartedRef = useRef(false);

  // Boot sequence messages (translated)
  const bootSequence = useMemo(() => [
    { text: t('auth.bootstrap.terminal.version'), delay: 0 },
    { text: t('auth.bootstrap.terminal.initializing'), delay: 400 },
    { text: t('auth.bootstrap.terminal.securityModules'), delay: 800 },
    { text: t('auth.bootstrap.terminal.databaseConnection'), delay: 1200 },
    { text: t('auth.bootstrap.terminal.cryptoReady'), delay: 1600 },
    { text: t('auth.bootstrap.terminal.noSuperAdmin'), delay: 2000 },
    { text: t('auth.bootstrap.terminal.protocolInitiated'), delay: 2400, highlight: true },
    { text: t('auth.bootstrap.terminal.awaitingCredentials'), delay: 2800 },
  ], [t]);

  // Redirect if bootstrap not needed
  useEffect(() => {
    if (status && !status.needsBootstrap) {
      navigate('/auth/login');
    }
  }, [status, navigate]);

  // Boot sequence animation
  useEffect(() => {
    if (bootStartedRef.current) return;
    bootStartedRef.current = true;

    bootSequence.forEach((msg, index) => {
      setTimeout(() => {
        setBootMessages(prev => [...prev, msg]);
        if (index === bootSequence.length - 1) {
          setTimeout(() => setBootComplete(true), 500);
        }
      }, msg.delay);
    });
  }, [bootSequence]);

  // Auto-scroll terminal
  useEffect(() => {
    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [bootMessages]);

  const validateForm = (): boolean => {
    if (!email || !password || !confirmPassword || !firstName || !lastName) {
      setError(t('auth.bootstrap.validation.allFieldsRequired'));
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError(t('auth.bootstrap.validation.invalidEmail'));
      return false;
    }
    if (password.length < 12) {
      setError(t('auth.bootstrap.validation.passwordMinLength'));
      return false;
    }
    if (password !== confirmPassword) {
      setError(t('auth.bootstrap.validation.passwordsNotMatch'));
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) return;

    setIsInitializing(true);

    // Simulate initialization progress
    const progressInterval = setInterval(() => {
      setInitProgress(prev => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return prev;
        }
        return prev + Math.random() * 15;
      });
    }, 200);

    try {
      await bootstrapMutation.mutateAsync({
        email,
        password,
        firstName,
        lastName,
      });
      setInitProgress(100);
      clearInterval(progressInterval);
    } catch (err) {
      clearInterval(progressInterval);
      setIsInitializing(false);
      setInitProgress(0);
      if (isApiError(err)) {
        setError(err.message);
      } else {
        setError(t('auth.bootstrap.errors.bootstrapFailed'));
      }
    }
  };

  // Loading state
  if (statusLoading) {
    return (
      <div className="bootstrap-loading">
        <div className="loading-spinner" />
        <p>{t('auth.bootstrap.checkingStatus')}</p>
        <style>{styles}</style>
      </div>
    );
  }

  // Error state - bootstrap not allowed
  if (statusError || (status && !status.bootstrapAllowed)) {
    return (
      <div className="bootstrap-error">
        <AlertTriangle size={48} />
        <h2>{t('auth.bootstrap.notAvailable')}</h2>
        <p>{t('auth.bootstrap.alreadyInitialized')}</p>
        <button onClick={() => navigate('/auth/login')}>{t('auth.bootstrap.goToLogin')}</button>
        <style>{styles}</style>
      </div>
    );
  }

  return (
    <div className="bootstrap-container">
      {/* Animated Background */}
      <div className="cyber-background">
        <div className="grid-overlay" />
        <div className="scan-line" />
        <div className="glow-orb glow-orb-1" />
        <div className="glow-orb glow-orb-2" />
        <div className="glow-orb glow-orb-3" />
        {/* Floating particles */}
        {[...Array(20)].map((_, i) => (
          <div key={i} className="particle" style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 5}s`,
            animationDuration: `${3 + Math.random() * 4}s`,
          }} />
        ))}
      </div>

      {/* Main Content */}
      <div className="bootstrap-content">
        {/* Header */}
        <div className="bootstrap-header">
          <div className="logo-container">
            <div className="logo-hex">
              <Shield size={32} />
            </div>
            <div className="logo-rings">
              <div className="ring ring-1" />
              <div className="ring ring-2" />
              <div className="ring ring-3" />
            </div>
          </div>
          <h1 className="title">
            <span className="title-main">{t('auth.bootstrap.title')}</span>
            <span className="title-sub">{t('auth.bootstrap.subtitle')}</span>
          </h1>
          {/* Language Toggle */}
          <div className="language-toggle-container">
            <LanguageToggle size="sm" />
          </div>
        </div>

        {/* Terminal Window */}
        <div className="terminal-window">
          <div className="terminal-header">
            <div className="terminal-dots">
              <span className="dot dot-red" />
              <span className="dot dot-yellow" />
              <span className="dot dot-green" />
            </div>
            <span className="terminal-title">
              <Terminal size={14} />
              boot_sequence.log
            </span>
          </div>
          <div className="terminal-body" ref={terminalRef}>
            {bootMessages.map((msg, i) => (
              <div
                key={i}
                className={`terminal-line ${msg.highlight ? 'highlight' : ''}`}
              >
                {msg.text}
                {i === bootMessages.length - 1 && !bootComplete && (
                  <span className="cursor">_</span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Bootstrap Form */}
        {bootComplete && (
          <div className={`bootstrap-form-container ${isInitializing ? 'initializing' : ''}`}>
            {isInitializing ? (
              <div className="init-progress">
                <div className="progress-header">
                  <Cpu className="spin" size={24} />
                  <span>{t('auth.bootstrap.progress.title')}</span>
                </div>
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{ width: `${initProgress}%` }}
                  />
                </div>
                <div className="progress-stats">
                  <div className="stat">
                    <Database size={16} />
                    <span>{t('auth.bootstrap.progress.creatingUser')}</span>
                  </div>
                  <div className="stat">
                    <Lock size={16} />
                    <span>{t('auth.bootstrap.progress.encryptingCredentials')}</span>
                  </div>
                  <div className="stat">
                    <Shield size={16} />
                    <span>{t('auth.bootstrap.progress.settingPermissions')}</span>
                  </div>
                </div>
                {initProgress >= 100 && (
                  <div className="init-complete">
                    <CheckCircle2 size={32} />
                    <span>{t('auth.bootstrap.progress.complete')}</span>
                  </div>
                )}
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bootstrap-form">
                <div className="form-section">
                  <h3 className="section-title">
                    <span className="section-icon">01</span>
                    {t('auth.bootstrap.form.identity')}
                  </h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label>{t('auth.bootstrap.form.firstName')}</label>
                      <div className="input-wrapper">
                        <input
                          type="text"
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          placeholder={t('auth.bootstrap.form.firstNamePlaceholder')}
                          autoComplete="given-name"
                        />
                        <div className="input-glow" />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>{t('auth.bootstrap.form.lastName')}</label>
                      <div className="input-wrapper">
                        <input
                          type="text"
                          value={lastName}
                          onChange={(e) => setLastName(e.target.value)}
                          placeholder={t('auth.bootstrap.form.lastNamePlaceholder')}
                          autoComplete="family-name"
                        />
                        <div className="input-glow" />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h3 className="section-title">
                    <span className="section-icon">02</span>
                    {t('auth.bootstrap.form.credentials')}
                  </h3>
                  <div className="form-group">
                    <label>{t('auth.bootstrap.form.email')}</label>
                    <div className="input-wrapper">
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder={t('auth.bootstrap.form.emailPlaceholder')}
                        autoComplete="email"
                      />
                      <div className="input-glow" />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>{t('auth.bootstrap.form.password')}</label>
                      <div className="input-wrapper">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder={t('auth.bootstrap.form.passwordPlaceholder')}
                          autoComplete="new-password"
                        />
                        <button
                          type="button"
                          className="toggle-password"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                        <div className="input-glow" />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>{t('auth.bootstrap.form.confirmPassword')}</label>
                      <div className="input-wrapper">
                        <input
                          type={showConfirmPassword ? 'text' : 'password'}
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          placeholder={t('auth.bootstrap.form.confirmPasswordPlaceholder')}
                          autoComplete="new-password"
                        />
                        <button
                          type="button"
                          className="toggle-password"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        >
                          {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                        <div className="input-glow" />
                      </div>
                    </div>
                  </div>
                </div>

                {error && (
                  <div className="error-message">
                    <AlertTriangle size={16} />
                    <span>{error}</span>
                  </div>
                )}

                <button type="submit" className="submit-button">
                  <span className="button-text">{t('auth.bootstrap.form.submitButton')}</span>
                  <span className="button-icon">
                    <Shield size={20} />
                  </span>
                  <div className="button-glow" />
                </button>

                <p className="security-notice">
                  <Lock size={14} />
                  <span>{t('auth.bootstrap.form.securityNotice')}</span>
                </p>
              </form>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="bootstrap-footer">
          <div className="footer-line" />
          <span>COREX PLATFORM &copy; {new Date().getFullYear()}</span>
          <div className="footer-line" />
        </div>
      </div>

      <style>{styles}</style>
    </div>
  );
}

// ============================================================================
// Styles
// ============================================================================

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;500;600;700;800;900&family=JetBrains+Mono:wght@300;400;500;600&display=swap');

  /* ═══════════════════════════════════════════════════════════════════════════
   * ROOT VARIABLES
   * ═══════════════════════════════════════════════════════════════════════════ */

  .bootstrap-container {
    --cyber-black: #0a0a0f;
    --cyber-dark: #12121a;
    --cyber-gray: #1a1a24;
    --cyber-cyan: #00f0ff;
    --cyber-cyan-dim: rgba(0, 240, 255, 0.3);
    --cyber-magenta: #ff00ff;
    --cyber-red: #ff0040;
    --cyber-green: #00ff88;
    --cyber-yellow: #ffcc00;
    --cyber-white: #e0e0e0;
    --font-display: 'Orbitron', sans-serif;
    --font-mono: 'JetBrains Mono', monospace;
  }

  /* ═══════════════════════════════════════════════════════════════════════════
   * LOADING & ERROR STATES
   * ═══════════════════════════════════════════════════════════════════════════ */

  .bootstrap-loading,
  .bootstrap-error {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: var(--cyber-black);
    color: var(--cyber-cyan);
    font-family: var(--font-mono);
    gap: 20px;
  }

  .loading-spinner {
    width: 50px;
    height: 50px;
    border: 3px solid var(--cyber-gray);
    border-top-color: var(--cyber-cyan);
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  .bootstrap-error h2 {
    font-family: var(--font-display);
    color: var(--cyber-red);
  }

  .bootstrap-error button {
    padding: 12px 24px;
    background: transparent;
    border: 1px solid var(--cyber-cyan);
    color: var(--cyber-cyan);
    font-family: var(--font-mono);
    cursor: pointer;
    transition: all 0.3s ease;
  }

  .bootstrap-error button:hover {
    background: var(--cyber-cyan);
    color: var(--cyber-black);
  }

  /* ═══════════════════════════════════════════════════════════════════════════
   * MAIN CONTAINER
   * ═══════════════════════════════════════════════════════════════════════════ */

  .bootstrap-container {
    min-height: 100vh;
    background: var(--cyber-black);
    position: relative;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  /* ═══════════════════════════════════════════════════════════════════════════
   * ANIMATED BACKGROUND
   * ═══════════════════════════════════════════════════════════════════════════ */

  .cyber-background {
    position: fixed;
    inset: 0;
    z-index: 0;
  }

  .grid-overlay {
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(rgba(0, 240, 255, 0.03) 1px, transparent 1px),
      linear-gradient(90deg, rgba(0, 240, 255, 0.03) 1px, transparent 1px);
    background-size: 50px 50px;
    animation: gridMove 20s linear infinite;
  }

  @keyframes gridMove {
    0% { transform: perspective(500px) rotateX(60deg) translateY(0); }
    100% { transform: perspective(500px) rotateX(60deg) translateY(50px); }
  }

  .scan-line {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg,
      transparent,
      var(--cyber-cyan-dim),
      var(--cyber-cyan),
      var(--cyber-cyan-dim),
      transparent
    );
    animation: scanDown 4s ease-in-out infinite;
    opacity: 0.5;
  }

  @keyframes scanDown {
    0%, 100% { top: -4px; opacity: 0; }
    10% { opacity: 0.5; }
    90% { opacity: 0.5; }
    100% { top: 100%; opacity: 0; }
  }

  .glow-orb {
    position: absolute;
    border-radius: 50%;
    filter: blur(100px);
    opacity: 0.15;
    animation: orbFloat 8s ease-in-out infinite;
  }

  .glow-orb-1 {
    width: 400px;
    height: 400px;
    background: var(--cyber-cyan);
    top: -100px;
    right: -100px;
    animation-delay: 0s;
  }

  .glow-orb-2 {
    width: 300px;
    height: 300px;
    background: var(--cyber-magenta);
    bottom: -50px;
    left: -50px;
    animation-delay: 2s;
  }

  .glow-orb-3 {
    width: 250px;
    height: 250px;
    background: var(--cyber-cyan);
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    animation-delay: 4s;
  }

  @keyframes orbFloat {
    0%, 100% { transform: translate(0, 0) scale(1); }
    50% { transform: translate(20px, -20px) scale(1.1); }
  }

  .particle {
    position: absolute;
    width: 3px;
    height: 3px;
    background: var(--cyber-cyan);
    border-radius: 50%;
    animation: particleFloat 5s ease-in-out infinite;
    opacity: 0.6;
  }

  @keyframes particleFloat {
    0%, 100% { transform: translateY(0) scale(1); opacity: 0.6; }
    50% { transform: translateY(-30px) scale(1.5); opacity: 1; }
  }

  /* ═══════════════════════════════════════════════════════════════════════════
   * MAIN CONTENT
   * ═══════════════════════════════════════════════════════════════════════════ */

  .bootstrap-content {
    position: relative;
    z-index: 10;
    width: 100%;
    max-width: 640px;
    padding: 40px 20px;
    display: flex;
    flex-direction: column;
    gap: 30px;
  }

  /* ═══════════════════════════════════════════════════════════════════════════
   * HEADER
   * ═══════════════════════════════════════════════════════════════════════════ */

  .bootstrap-header {
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
    position: relative;
  }

  .language-toggle-container {
    position: absolute;
    top: 0;
    right: -80px;
  }

  @media (max-width: 700px) {
    .language-toggle-container {
      position: static;
      margin-top: 10px;
    }
  }

  .logo-container {
    position: relative;
    width: 80px;
    height: 80px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .logo-hex {
    width: 60px;
    height: 60px;
    background: linear-gradient(135deg, var(--cyber-cyan), var(--cyber-magenta));
    clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--cyber-black);
    animation: hexPulse 2s ease-in-out infinite;
    z-index: 2;
  }

  @keyframes hexPulse {
    0%, 100% { filter: drop-shadow(0 0 10px var(--cyber-cyan)); }
    50% { filter: drop-shadow(0 0 25px var(--cyber-cyan)); }
  }

  .logo-rings {
    position: absolute;
    inset: 0;
  }

  .ring {
    position: absolute;
    border: 1px solid var(--cyber-cyan);
    border-radius: 50%;
    opacity: 0.3;
    animation: ringExpand 3s ease-out infinite;
  }

  .ring-1 { inset: 0; animation-delay: 0s; }
  .ring-2 { inset: -10px; animation-delay: 1s; }
  .ring-3 { inset: -20px; animation-delay: 2s; }

  @keyframes ringExpand {
    0% { transform: scale(0.8); opacity: 0.5; }
    100% { transform: scale(1.5); opacity: 0; }
  }

  .title {
    display: flex;
    flex-direction: column;
    gap: 5px;
  }

  .title-main {
    font-family: var(--font-display);
    font-size: 36px;
    font-weight: 800;
    letter-spacing: 8px;
    background: linear-gradient(90deg, var(--cyber-cyan), var(--cyber-white), var(--cyber-cyan));
    background-size: 200% 100%;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: textShine 3s ease-in-out infinite;
  }

  @keyframes textShine {
    0%, 100% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
  }

  .title-sub {
    font-family: var(--font-mono);
    font-size: 12px;
    letter-spacing: 4px;
    color: var(--cyber-cyan);
    opacity: 0.7;
  }

  /* ═══════════════════════════════════════════════════════════════════════════
   * TERMINAL WINDOW
   * ═══════════════════════════════════════════════════════════════════════════ */

  .terminal-window {
    background: rgba(10, 10, 15, 0.9);
    border: 1px solid rgba(0, 240, 255, 0.2);
    border-radius: 8px;
    overflow: hidden;
    box-shadow:
      0 0 20px rgba(0, 240, 255, 0.1),
      inset 0 0 30px rgba(0, 0, 0, 0.5);
  }

  .terminal-header {
    background: rgba(0, 240, 255, 0.05);
    padding: 10px 15px;
    display: flex;
    align-items: center;
    gap: 15px;
    border-bottom: 1px solid rgba(0, 240, 255, 0.1);
  }

  .terminal-dots {
    display: flex;
    gap: 6px;
  }

  .dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
  }

  .dot-red { background: var(--cyber-red); }
  .dot-yellow { background: var(--cyber-yellow); }
  .dot-green { background: var(--cyber-green); }

  .terminal-title {
    font-family: var(--font-mono);
    font-size: 12px;
    color: var(--cyber-white);
    opacity: 0.6;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .terminal-body {
    padding: 15px;
    max-height: 200px;
    overflow-y: auto;
    font-family: var(--font-mono);
    font-size: 13px;
    line-height: 1.8;
  }

  .terminal-line {
    color: var(--cyber-green);
    animation: fadeIn 0.3s ease;
  }

  .terminal-line.highlight {
    color: var(--cyber-cyan);
    font-weight: 600;
    text-shadow: 0 0 10px var(--cyber-cyan);
  }

  .cursor {
    display: inline-block;
    animation: blink 0.8s step-end infinite;
    color: var(--cyber-cyan);
  }

  @keyframes blink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0; }
  }

  @keyframes fadeIn {
    from { opacity: 0; transform: translateX(-10px); }
    to { opacity: 1; transform: translateX(0); }
  }

  /* ═══════════════════════════════════════════════════════════════════════════
   * FORM CONTAINER
   * ═══════════════════════════════════════════════════════════════════════════ */

  .bootstrap-form-container {
    background: rgba(18, 18, 26, 0.95);
    border: 1px solid rgba(0, 240, 255, 0.15);
    border-radius: 12px;
    padding: 30px;
    animation: formSlideIn 0.5s ease;
    position: relative;
    overflow: hidden;
  }

  .bootstrap-form-container::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg,
      transparent,
      var(--cyber-cyan),
      var(--cyber-magenta),
      var(--cyber-cyan),
      transparent
    );
    animation: borderGlow 2s linear infinite;
  }

  @keyframes borderGlow {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }

  @keyframes formSlideIn {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }

  /* ═══════════════════════════════════════════════════════════════════════════
   * FORM ELEMENTS
   * ═══════════════════════════════════════════════════════════════════════════ */

  .bootstrap-form {
    display: flex;
    flex-direction: column;
    gap: 25px;
  }

  .form-section {
    display: flex;
    flex-direction: column;
    gap: 15px;
  }

  .section-title {
    font-family: var(--font-display);
    font-size: 11px;
    letter-spacing: 3px;
    color: var(--cyber-cyan);
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 5px;
  }

  .section-icon {
    width: 28px;
    height: 28px;
    background: rgba(0, 240, 255, 0.1);
    border: 1px solid var(--cyber-cyan);
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: var(--font-mono);
    font-size: 10px;
  }

  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 15px;
  }

  @media (max-width: 500px) {
    .form-row {
      grid-template-columns: 1fr;
    }
  }

  .form-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .form-group label {
    font-family: var(--font-mono);
    font-size: 10px;
    letter-spacing: 2px;
    color: var(--cyber-white);
    opacity: 0.6;
  }

  .input-wrapper {
    position: relative;
  }

  .input-wrapper input {
    width: 100%;
    padding: 14px 16px;
    background: rgba(0, 0, 0, 0.4);
    border: 1px solid rgba(0, 240, 255, 0.2);
    border-radius: 6px;
    color: var(--cyber-white);
    font-family: var(--font-mono);
    font-size: 14px;
    transition: all 0.3s ease;
    outline: none;
  }

  .input-wrapper input::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }

  .input-wrapper input:focus {
    border-color: var(--cyber-cyan);
    box-shadow: 0 0 15px rgba(0, 240, 255, 0.2);
  }

  .input-glow {
    position: absolute;
    inset: 0;
    border-radius: 6px;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.3s ease;
    background: linear-gradient(90deg,
      transparent,
      rgba(0, 240, 255, 0.1),
      transparent
    );
  }

  .input-wrapper input:focus + .toggle-password + .input-glow,
  .input-wrapper input:focus + .input-glow {
    opacity: 1;
    animation: inputGlowMove 2s linear infinite;
  }

  @keyframes inputGlowMove {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }

  .toggle-password {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    color: var(--cyber-cyan);
    cursor: pointer;
    opacity: 0.6;
    transition: opacity 0.2s;
    padding: 4px;
  }

  .toggle-password:hover {
    opacity: 1;
  }

  /* ═══════════════════════════════════════════════════════════════════════════
   * ERROR MESSAGE
   * ═══════════════════════════════════════════════════════════════════════════ */

  .error-message {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 16px;
    background: rgba(255, 0, 64, 0.1);
    border: 1px solid rgba(255, 0, 64, 0.3);
    border-radius: 6px;
    color: var(--cyber-red);
    font-family: var(--font-mono);
    font-size: 13px;
    animation: shake 0.3s ease;
  }

  @keyframes shake {
    0%, 100% { transform: translateX(0); }
    25% { transform: translateX(-5px); }
    75% { transform: translateX(5px); }
  }

  /* ═══════════════════════════════════════════════════════════════════════════
   * SUBMIT BUTTON
   * ═══════════════════════════════════════════════════════════════════════════ */

  .submit-button {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 16px 32px;
    background: linear-gradient(135deg,
      rgba(0, 240, 255, 0.2),
      rgba(255, 0, 255, 0.1)
    );
    border: 1px solid var(--cyber-cyan);
    border-radius: 8px;
    color: var(--cyber-cyan);
    font-family: var(--font-display);
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 3px;
    cursor: pointer;
    overflow: hidden;
    transition: all 0.3s ease;
    margin-top: 10px;
  }

  .submit-button:hover {
    background: linear-gradient(135deg,
      rgba(0, 240, 255, 0.3),
      rgba(255, 0, 255, 0.2)
    );
    box-shadow:
      0 0 30px rgba(0, 240, 255, 0.3),
      inset 0 0 20px rgba(0, 240, 255, 0.1);
    transform: translateY(-2px);
  }

  .submit-button:active {
    transform: translateY(0);
  }

  .button-glow {
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg,
      transparent,
      rgba(0, 240, 255, 0.4),
      transparent
    );
    transform: translateX(-100%);
    transition: transform 0.5s ease;
  }

  .submit-button:hover .button-glow {
    transform: translateX(100%);
  }

  .button-icon {
    display: flex;
    align-items: center;
    animation: iconPulse 1.5s ease-in-out infinite;
  }

  @keyframes iconPulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.1); }
  }

  /* ═══════════════════════════════════════════════════════════════════════════
   * SECURITY NOTICE
   * ═══════════════════════════════════════════════════════════════════════════ */

  .security-notice {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-family: var(--font-mono);
    font-size: 11px;
    color: var(--cyber-white);
    opacity: 0.4;
    margin-top: 5px;
  }

  /* ═══════════════════════════════════════════════════════════════════════════
   * INITIALIZATION PROGRESS
   * ═══════════════════════════════════════════════════════════════════════════ */

  .init-progress {
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 20px 0;
  }

  .progress-header {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    font-family: var(--font-display);
    font-size: 14px;
    letter-spacing: 2px;
    color: var(--cyber-cyan);
  }

  .spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  .progress-bar {
    height: 4px;
    background: rgba(0, 240, 255, 0.1);
    border-radius: 2px;
    overflow: hidden;
  }

  .progress-fill {
    height: 100%;
    background: linear-gradient(90deg, var(--cyber-cyan), var(--cyber-magenta));
    border-radius: 2px;
    transition: width 0.3s ease;
    box-shadow: 0 0 10px var(--cyber-cyan);
  }

  .progress-stats {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .stat {
    display: flex;
    align-items: center;
    gap: 10px;
    font-family: var(--font-mono);
    font-size: 12px;
    color: var(--cyber-white);
    opacity: 0.6;
    animation: statFade 0.5s ease forwards;
  }

  .stat:nth-child(2) { animation-delay: 0.3s; }
  .stat:nth-child(3) { animation-delay: 0.6s; }

  @keyframes statFade {
    from { opacity: 0; transform: translateX(-10px); }
    to { opacity: 0.6; transform: translateX(0); }
  }

  .init-complete {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 20px;
    background: rgba(0, 255, 136, 0.1);
    border: 1px solid rgba(0, 255, 136, 0.3);
    border-radius: 8px;
    color: var(--cyber-green);
    font-family: var(--font-display);
    font-size: 14px;
    letter-spacing: 2px;
    animation: completeSlide 0.5s ease;
  }

  @keyframes completeSlide {
    from { opacity: 0; transform: scale(0.9); }
    to { opacity: 1; transform: scale(1); }
  }

  /* ═══════════════════════════════════════════════════════════════════════════
   * FOOTER
   * ═══════════════════════════════════════════════════════════════════════════ */

  .bootstrap-footer {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 20px;
    font-family: var(--font-mono);
    font-size: 11px;
    color: var(--cyber-white);
    opacity: 0.3;
  }

  .footer-line {
    flex: 1;
    height: 1px;
    background: linear-gradient(90deg,
      transparent,
      rgba(0, 240, 255, 0.3),
      transparent
    );
  }

  /* ═══════════════════════════════════════════════════════════════════════════
   * SCROLLBAR
   * ═══════════════════════════════════════════════════════════════════════════ */

  .terminal-body::-webkit-scrollbar {
    width: 6px;
  }

  .terminal-body::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.3);
  }

  .terminal-body::-webkit-scrollbar-thumb {
    background: rgba(0, 240, 255, 0.3);
    border-radius: 3px;
  }

  .terminal-body::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 240, 255, 0.5);
  }
`;

export default BootstrapPage;
